/**
 * ContractBuilderPage
 * 
 * Public-facing page for AI-assisted contract creation.
 * New flow: Collect all template variables upfront via multi-step form,
 * then send email with editor access link.
 */

import { useState } from 'react';
import { ContractVariablesForm } from '@/components/contract-builder/ContractVariablesForm';
import { FormSubmittedSuccess } from '@/components/contract-builder/FormSubmittedSuccess';
import PublicNavBar from '@/components/layout/PublicNavBar';
import ApiService from '@/services/api';
import { EndpointKeys } from '@/config/api';
import { useToast } from '@/components/ui/use-toast';

type PageState = 'form' | 'submitted';

export const ContractBuilderPage: React.FC = () => {
  const [pageState, setPageState] = useState<PageState>('form');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleFormSubmit = async (formData: any) => {
    setIsSubmitting(true);

    try {
      // Generate session ID
      const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Map form data to structured_data format expected by backend
      const structured_data = {
        employer_name: formData.employer_name,
        employer_address: formData.employer_address,
        employee_name: formData.employee_name,
        employee_address: formData.employee_address,
        start_date: formatDate(formData.start_date),
        probation_period: formData.probation_period,
        work_address: formData.work_address,
        weekly_hours: formData.weekly_hours,
        work_start_time: formData.work_start_time,
        work_end_time: formData.work_end_time,
        work_days: formData.work_days,
        salary_amount: formData.salary_amount,
        payment_date: formData.payment_date,
        payment_method: formData.payment_method,
        overtime_rate: formData.overtime_rate,
        annual_leave_weeks: formData.annual_leave_weeks,
        bank_holidays_included: formData.bank_holidays_included,
        notice_period: formData.notice_period,
        children_names: formData.children_names || '',
        children_ages: formData.children_ages || '',
        additional_duties: formData.additional_duties || '',
      };

      const response = await ApiService.request<{
        success: boolean;
        message: string;
        contract_id: string;
        email_sent_to: string;
      }>(EndpointKeys.CONTRACT_BUILDER_CREATE, {
        method: 'POST',
        body: {
          user_email: formData.email,
          user_first_name: formData.first_name,
          user_last_name: formData.last_name,
          session_id: sessionId,
          structured_data,
        },
        skipAuth: true,
      });

      if (response.success) {
        setSubmittedEmail(formData.email);
        setPageState('submitted');
        
        toast({
          title: 'Success!',
          description: response.message,
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Failed to create contract. Please try again.',
        });
      }
    } catch (error: any) {
      console.error('Error creating contract:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to create contract. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to format date to UK format for display
  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('en-GB', { month: 'long' });
    const year = date.getFullYear();
    
    // Add ordinal suffix to day
    const suffix = ['th', 'st', 'nd', 'rd'][
      day % 10 > 3 ? 0 : (((day % 100) - (day % 10) !== 10) ? 1 : 0) * (day % 10)
    ];
    
    return `${day}${suffix} ${month} ${year}`;
  };

  if (pageState === 'submitted') {
    return (
      <>
        <PublicNavBar />
        <FormSubmittedSuccess email={submittedEmail} />
      </>
    );
  }

  return (
    <>
      <PublicNavBar />
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Create Your Employment Contract
            </h1>
            <p className="text-lg text-gray-600">
              Fill in the details below and we'll create your contract with AI-powered editing
            </p>
          </div>

          {/* Form */}
          <ContractVariablesForm
            onSubmit={handleFormSubmit}
            isSubmitting={isSubmitting}
          />
        </div>
      </div>
    </>
  );
};
